import type { ActivityContext, ActivityHandle } from '../../core/activity';
import levelsData from './levels.json';
import puzzlesData from './puzzles.json';

interface Puzzle {
  puzzle: string;
  solutions: string[];
  complexity: number;
}

interface SubLevel {
  count: number;
  complexities: number[];
  operatorsCount: number;
}

interface Level {
  level: number;
  objective: string;
  difficulty: number;
  data: SubLevel[];
}

interface LevelsFile {
  levels: Level[];
}

type Operator = '+' | '−' | '×' | '÷';

interface Card {
  idx: number;
  value: number;
  used: boolean;
}

interface Operation {
  fromIdx: number;
  fromValue: number;
  toIdx: number;
  toValue: number;
  resultValue: number;
  stepText: string;
}

const LEVELS = (levelsData as LevelsFile).levels;
const PUZZLES = puzzlesData as Puzzle[];
const ALL_OPS: Operator[] = ['+', '−', '×', '÷'];

const OBJECTIVE_PT: Record<string, string> = {
  'Solvable with + and -.': 'Use só + e −.',
  'Solvable with +, - and ×.': 'Use +, − e ×.',
  'Multiplication required.': 'A multiplicação é obrigatória.',
  'Solvable with all operators.': 'Pode usar +, −, × e ÷.',
  'Division required.': 'A divisão é obrigatória.',
  'All operators required.': 'Use todos os operadores.',
};

function shuffle<T>(arr: T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = a[i]!;
    a[i] = a[j]!;
    a[j] = tmp;
  }
  return a;
}

function pickPuzzles(level: Level): Puzzle[] {
  const cfg = level.data[0]!;
  const pool = PUZZLES.filter((p) => cfg.complexities.includes(p.complexity));
  return shuffle(pool).slice(0, cfg.count);
}

function compute(a: number, op: Operator, b: number): number {
  switch (op) {
    case '+': return a + b;
    case '−': return a - b;
    case '×': return a * b;
    case '÷': return b === 0 ? Number.NaN : a / b;
  }
}

export function mountGuess24(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  const cfg = level.data[0]!;
  const ops: Operator[] = ALL_OPS.slice(0, cfg.operatorsCount);
  const objectivePt = OBJECTIVE_PT[level.objective] ?? level.objective;

  let puzzles: Puzzle[] = pickPuzzles(level);
  let subIndex = 0;
  let correctCount = 0;
  let cards: Card[] = [];
  let operations: Operation[] = [];
  let selectedCardIdx: number | null = null;
  let selectedOp: Operator | null = null;
  let buttonsBlocked = false;
  let lastInvalid = false;
  let invalidTimer: number | null = null;

  const totalSubs = puzzles.length;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Adivinhe 24</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage g24">
      <div class="feedback" aria-live="polite"></div>
      <p class="g24__progress" aria-live="polite"></p>
      <p class="g24__hint">${objectivePt}</p>
      <div class="g24__cards" role="list"></div>
      <div class="g24__operators" role="list"></div>
      <div class="g24__steps" aria-live="polite"></div>
      <div class="g24__actions">
        <button class="btn btn--secondary g24__undo" type="button">↶ Desfazer</button>
        <button class="btn btn--secondary g24__reset" type="button">↺ Reiniciar</button>
      </div>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.g24__progress')!;
  const cardsEl = page.querySelector<HTMLElement>('.g24__cards')!;
  const opsEl = page.querySelector<HTMLElement>('.g24__operators')!;
  const stepsEl = page.querySelector<HTMLElement>('.g24__steps')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const undoBtn = page.querySelector<HTMLButtonElement>('.g24__undo')!;
  const resetBtn = page.querySelector<HTMLButtonElement>('.g24__reset')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());

  undoBtn.addEventListener('click', () => {
    if (buttonsBlocked) return;
    undoLastOperation();
  });
  resetBtn.addEventListener('click', () => {
    if (buttonsBlocked) return;
    initRound();
  });

  function initRound(): void {
    if (invalidTimer !== null) {
      window.clearTimeout(invalidTimer);
      invalidTimer = null;
    }
    const puzzle = puzzles[subIndex]!;
    const values = shuffle(puzzle.puzzle.split(' ').map((v) => parseInt(v, 10)));
    cards = values.map((value, idx) => ({ idx, value, used: false }));
    operations = [];
    selectedCardIdx = null;
    selectedOp = null;
    buttonsBlocked = false;
    lastInvalid = false;
    renderProgress();
    renderAll();
  }

  function renderProgress(): void {
    progressEl.textContent = `Pergunta ${subIndex + 1} de ${totalSubs}`;
  }

  function renderAll(): void {
    renderCards();
    renderOps();
    renderSteps(lastInvalid);
    undoBtn.disabled = operations.length === 0 || buttonsBlocked;
  }

  function renderCards(): void {
    cardsEl.innerHTML = '';
    cards.forEach((c) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'g24__card';
      if (c.used) btn.classList.add('g24__card--used');
      if (selectedCardIdx === c.idx) btn.classList.add('g24__card--selected');
      btn.dataset['idx'] = String(c.idx);
      btn.textContent = String(c.value);
      btn.setAttribute('aria-label', `Carta ${c.value}`);
      btn.disabled = c.used || buttonsBlocked;
      btn.addEventListener('click', () => onCardClick(c.idx));
      cardsEl.appendChild(btn);
    });
  }

  function renderOps(): void {
    opsEl.innerHTML = '';
    ops.forEach((op) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'g24__op';
      if (selectedOp === op) btn.classList.add('g24__op--selected');
      btn.textContent = op;
      btn.setAttribute('aria-label', `Operador ${op}`);
      btn.disabled = buttonsBlocked;
      btn.addEventListener('click', () => onOpClick(op));
      opsEl.appendChild(btn);
    });
  }

  function renderSteps(invalidLast = false): void {
    const lines = operations.map((o, i) => {
      const cls = invalidLast && i === operations.length - 1 ? 'g24__step--invalid' : '';
      return cls ? `<span class="${cls}">${escape(o.stepText)}</span>` : escape(o.stepText);
    });
    stepsEl.innerHTML = lines.join('\n');
  }

  function escape(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function onCardClick(idx: number): void {
    if (buttonsBlocked) return;
    const c = cards[idx];
    if (!c || c.used) return;
    if (selectedCardIdx === null) {
      selectedCardIdx = idx;
      ctx.audio.play('click');
      renderAll();
      return;
    }
    if (selectedCardIdx === idx) {
      selectedCardIdx = null;
      selectedOp = null;
      ctx.audio.play('click');
      renderAll();
      return;
    }
    if (selectedOp === null) {
      selectedCardIdx = idx;
      ctx.audio.play('click');
      renderAll();
      return;
    }
    performOperation(selectedCardIdx, selectedOp, idx);
  }

  function onOpClick(op: Operator): void {
    if (buttonsBlocked) return;
    if (selectedCardIdx === null) return;
    if (selectedOp === op) {
      selectedOp = null;
    } else {
      selectedOp = op;
    }
    ctx.audio.play('click');
    renderAll();
  }

  function performOperation(fromIdx: number, op: Operator, toIdx: number): void {
    const a = cards[fromIdx]!.value;
    const b = cards[toIdx]!.value;
    const raw = compute(a, op, b);
    const isInteger = Number.isFinite(raw) && Math.floor(raw) === raw;
    const isValid = isInteger && raw >= 0;
    const displayResult = isInteger ? String(raw) : trimFloat(raw);
    const stepText = `${a} ${op} ${b} = ${displayResult}`;
    operations.push({ fromIdx, fromValue: a, toIdx, toValue: b, resultValue: raw, stepText });

    if (!isValid) {
      buttonsBlocked = true;
      lastInvalid = true;
      ctx.audio.play('wrong');
      renderAll();
      flashCard(toIdx);
      invalidTimer = window.setTimeout(() => {
        invalidTimer = null;
        operations.pop();
        selectedOp = null;
        buttonsBlocked = false;
        lastInvalid = false;
        renderAll();
      }, 900);
      return;
    }

    cards[fromIdx]!.value = raw;
    cards[toIdx]!.used = true;
    selectedCardIdx = fromIdx;
    selectedOp = null;
    ctx.audio.play('click');
    renderAll();

    const remaining = cards.filter((c) => !c.used).length;
    if (remaining === 1) {
      buttonsBlocked = true;
      const finalValue = cards[fromIdx]!.value;
      if (finalValue === 24) {
        ctx.audio.play('correct');
        showFeedback('ok');
        correctCount++;
        subIndex++;
        window.setTimeout(() => {
          if (subIndex < totalSubs) initRound();
          else finishLevel();
        }, 850);
      } else {
        ctx.audio.play('wrong');
        showFeedback('err');
        window.setTimeout(() => {
          buttonsBlocked = false;
          renderAll();
        }, 900);
      }
    }
  }

  function undoLastOperation(): void {
    if (operations.length === 0) return;
    const last = operations.pop()!;
    cards[last.fromIdx]!.value = last.fromValue;
    cards[last.toIdx]!.value = last.toValue;
    cards[last.toIdx]!.used = false;
    selectedCardIdx = null;
    selectedOp = null;
    ctx.audio.play('click');
    renderAll();
  }

  function trimFloat(n: number): string {
    if (!Number.isFinite(n)) return '?';
    return Number.parseFloat(n.toFixed(3)).toString();
  }

  function flashCard(idx: number): void {
    const el = cardsEl.querySelector<HTMLElement>(`[data-idx="${idx}"]`);
    if (!el) return;
    el.classList.add('g24__card--flash');
    window.setTimeout(() => el.classList.remove('g24__card--flash'), 900);
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 500);
  }

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, correctCount);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você resolveu ${correctCount} de ${totalSubs} desafios.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/guess24/${level.level + 1}`;
      });
    }
  }

  initRound();

  return {
    unmount(): void {
      if (invalidTimer !== null) {
        window.clearTimeout(invalidTimer);
        invalidTimer = null;
      }
      ctx.root.innerHTML = '';
    },
  };
}
